import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Container } from "reactstrap";
import iconSrc from '~/assets/images/desktop/insurance-screen-icon.svg';
import progressSrc from '~/assets/images/desktop/progress-bar-complete.png';
import DesktopMenu from "~/components/Shared/DesktopMenu";
import menuSteps from "~/constants/menuSteps";
import insuranceSrc from "~/assets/images/desktop/insurance-screen-banner.png";
import { randomStringSync, showError, showSuccess } from "~/helpers/utilHelper";
import { submitSqueezeVerification, updateCustomerInsuranceStatus, updateCustomerSqueezeId, logSignerOpenedInsuranceLink } from "~/helpers/backendHelper";
import { useNavigator } from "~/context/navigator";
import { useNavigate } from "react-router-dom";
import config from "~/config";
import { useAuth } from "~/context/auth";
import { route } from "~/helpers/routeHelper";
import CardPreloader from "~/components/Shared/CardPreloader";
import { ORDER_SIGNER_SQUEEZE_ID_ALREADY_EXISTS } from "~/helpers/errorHelper";
import { TRoute } from "~/types/model";
import Confirmation from "~/components/Shared/Confirmation";
import Order from "~/model/order";

declare const InsVerSDK: any;

const DesktopContent = () => {

  // navigator context that helps determine the next available route
  const { getNextRoute, setRoutes, order, isLoadingOrder } = useNavigator() as any;
  // router hook that helps redirect
  const navigate = useNavigate();
  // auth context
  const { user, refreshAuthUser } = useAuth();

  // flag to indicate if the insurance verification has been completed
  const [insuranceVerificationCompleted, setInsuranceVerificationCompleted] = useState(false);
  // flag to indicate if the insurance verification is in progress
  const [isInsuranceVerificationInProgress, setIsInsuranceVerificationInProgress] = useState(false);
  // make body overflow hidden when sdk is open
  const [isSdkOpen, setIsSdkOpen] = useState(false);
  // create sqzId for the squeeze verification
  const [sqzId, setSqzId] = useState<string | null>(null);
  // flag to indicate if the No confirmation modal is visible
  const [isNoConfVisible, setIsNoConfVisible] = useState(false);

  // flag to indicate if the user has completed the insurance verification
  const userCompletedInsuranceVerification = user?.hasInsurance !== null;

  // function that initializes the Squeeze SDK
  // this function is called when the user clicks the "Yes" button to verify their insurance
  // we load the Squeeze SDK script and render the verification UI in the div with id "ins-ver-container"
  const initializeSqueezeSdk = () => {
    setIsSdkOpen(true);
    // Load the Squeeze SDK script
    InsVerSDK.init({
      clientId: config.SQUEEZE_CLIENT_ID,
      environment: config.SQUEEZE_ENV,
      metadata: {
        sqzId: sqzId,
      },
      branding: {
        logoUrl: 'https://mavsign.com/static/logo-25c3dc125cfdfeee72c1fdff1ece1c7a.svg',
        overlayBg: "rgba(0, 0, 0, 0.91)",
        primaryColor: "#556EE6",
        backgroundColor: "#EEF0FD",
      },
      onComplete: (verificationId: string) => {
        setIsSdkOpen(false);
        sendSqueezeId(verificationId)
      },
      onError: (_error: any) => {
        showError("An error occurred during the insurance verification process. Please try again later.")
      },
      onClose: () => {
        setIsSdkOpen(false);
      }
    });
  };

  // function that sends the squeeze verification id to the backend
  // this function is called when the Squeeze SDK triggers the onComplete event
  // after the user has successfully completed the insurance verification process
  // we then redirect the user to the next step in the process
  const sendSqueezeId = (squeezeVerificationId: string) => {
    setIsInsuranceVerificationInProgress(true);
    submitSqueezeVerification({ squeezeVerificationId: squeezeVerificationId })
      .then(async _res => {
        showSuccess("Insurance verification completed successfully.");
        // Update the additional information route to false because in this scenario we need to skip it
        setRoutes((prevRoutes: TRoute[]) => {
          const ROUTE_ADDITIONAL_INFORMATION = route('additional');
          const secondFormOfId = prevRoutes.find((route: any) => route.route === ROUTE_ADDITIONAL_INFORMATION)
          if (secondFormOfId) {
            secondFormOfId.status = false;
          }
          return prevRoutes;
        })
        // insurance verification has been completed
        setInsuranceVerificationCompleted(true);
      })
      .catch(_err => {
        showError("An error occurred while sending the Squeeze ID. Please try again later.");
      })
      .finally(() => {
        setIsInsuranceVerificationInProgress(false);
      })
  };

  // function that handles the insurance decline
  const handleInsuranceDecline = () => {
    setIsInsuranceVerificationInProgress(true);
    updateCustomerInsuranceStatus({ hasInsurance: false })
      .then(_res => {
        // redirect to the next step in the process
        navigate(getNextRoute());
        // refresh the auth user
        refreshAuthUser()
      })
      .catch(_err => {
        showError("An error occurred. Please try again later.");
      })
      .finally(() => {
        setIsInsuranceVerificationInProgress(false);
      })
  };

  const updateSignerSqueezeId = (numOfTries = 1) => {
    const newSqzId = randomStringSync(5);
    updateCustomerSqueezeId({ squeezeId: newSqzId })
      .then(_res => {
        setSqzId(newSqzId);
      })
      .catch(err => {
        if (numOfTries === 10) {
          showError("An error occured. Please try again later!")
          return;
        }
        // if squeeze already exists try again until a uniqu code is generated
        if (err.code === ORDER_SIGNER_SQUEEZE_ID_ALREADY_EXISTS) {
          updateSignerSqueezeId(numOfTries + 1);
        }
      })
  }

  const isOrderInsuranceVerificationOnly = () => {
    if (order.isNotaryRequired || order.isEsignRequired || order.isInkSignRequired || order.isVidRequired) return false;
    return true;
  }

  // if the user navigates away from the insurance page, we need to refresh the auth user
  useEffect(() => {
    return () => {
      refreshAuthUser();
    }
  }, [])

  useEffect(() => {
    if (isSdkOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isSdkOpen]);

  useEffect(() => {
    if (!user) return;
    // if the user has a squeezeId, set it otherwise generate a new one and update the user
    if (!!user.squeezeId) {
      setSqzId(user.squeezeId);
    } else {
      updateSignerSqueezeId();
    }
  }, [user]);

  useEffect(() => {
    if (order?.isInsuranceVerificationRequired && order?.status !== Order.STATUS_COMPLETED) {
      logSignerOpenedInsuranceLink();
    }
  }, [order]);

  return <React.Fragment>
    <DesktopMenu title={"Insurance"} activeStep={menuSteps.INSURANCE} />
    <Container>
      <div className="justify-content-center d-flex">
        <img className="header-image-finish-desktop" src={insuranceSrc} />
      </div>
      <Row className="justify-content-center">
        <Col md={8} lg={6} xl={6} className="finish-column desktop">
          <Card className="mb-2">
            <CardBody>
              {
                order?.status === Order.STATUS_COMPLETED && isOrderInsuranceVerificationOnly() ?
                  <>
                    <div className="pt-2 text-center">
                      <img className="mt-3 verif-icon" src={iconSrc}></img>
                      <h5 className="mt-5 mb-4 title-card-mobile">Insurance Verification Unavailable</h5>
                      <img src={progressSrc}></img>
                      <p className="font-size-16 fw-light mb-2 card-description mt-4">Insurance verification is no longer available for this order.</p>
                      <p className="font-size-16 fw-bolder mb-2 card-description mt-2">Click Continue to go to the next step in the process.</p>
                    </div>
                    <div className="d-flex justify-content-center  mt-4">
                      <button className="btn bg-primary text-light w-50" onClick={() => navigate(getNextRoute())}>Continue</button>
                    </div>
                  </>
                  :
                  <>
                    {
                      userCompletedInsuranceVerification ?
                        <>
                          <>
                            <div className="pt-2 text-center">
                              <img className="mt-3 verif-icon" src={iconSrc}></img>
                              <h5 className="mt-5 mb-4 title-card-mobile">Insurance Verification Completed</h5>
                              <img src={progressSrc}></img>
                              <p className="font-size-16 fw-light mb-2 card-description mt-4">You have already completed the insurance verification process.</p>
                              <p className="font-size-16 fw-bolder mb-2 card-description mt-2">Click Continue to go to the next step in the process.</p>
                            </div>
                            <div className="d-flex justify-content-center  mt-4">
                              <button className="btn bg-primary text-light w-50" onClick={() => navigate(getNextRoute())}>Continue</button>
                            </div>
                          </>
                        </>
                        :
                        <>
                          {
                            !!insuranceVerificationCompleted &&
                            <>
                              <div className="pt-2 text-center">
                                <img className="mt-3 verif-icon" src={iconSrc}></img>
                                <h5 className="mt-5 mb-4 title-card-mobile">Insurance Verification Successful</h5>
                                <img src={progressSrc}></img>
                                <p className="font-size-16 fw-light mb-2 card-description mt-4">Your insurance details have been verified successfully.</p>
                                <p className="font-size-16 fw-bolder mb-2 card-description mt-2">Click Continue to go to the next step in the process.</p>
                              </div>
                              <div className="d-flex justify-content-center  mt-4">
                                <button className="btn bg-primary text-light w-50" onClick={() => { navigate(getNextRoute()); refreshAuthUser(); }}>Continue</button>
                              </div>
                            </>
                          }
                          {
                            !insuranceVerificationCompleted &&
                            <>
                              <div className="pt-2 text-center">
                                <img className="mt-3 verif-icon" src={iconSrc}></img>
                                <h5 className="mt-5 mb-4 title-card-mobile">Verify your insurance</h5>
                                <img src={progressSrc}></img>
                                <p className="font-size-16 fw-light mb-2 card-description mt-4">Next step in your order: We’ll quickly verify your insurance details. This only takes a few minutes and helps keep your documents secure.</p>
                                <p className="font-size-16 fw-bolder mb-2 card-description mt-2">Do you currently have an active insurance policy?</p>
                              </div>
                              <div className="d-flex justify-content-around gap-5 mt-4">
                                <button className="btn bg-primary text-light w-100" onClick={initializeSqueezeSdk}>
                                  Yes
                                </button>
                                <button className="btn bg-primary text-light w-100" onClick={() => setIsNoConfVisible(true)}>
                                  No
                                </button>
                              </div>
                            </>
                          }
                        </>
                    }
                  </>
              }
            </CardBody>
            {isNoConfVisible && <Confirmation
              confirmBtnText="Skip verification"
              confirmBtnBsStyle="danger"
              cancelBtnText="Continue with verification"
              cancelBtnBsStyle="primary-small"
              onConfirm={() => {
                setIsNoConfVisible(false);
                handleInsuranceDecline();
              }}
              onCancel={() => setIsNoConfVisible(false)}>
              Are you sure you want to skip insurance verification? Your dealership requires active insurance before delivering the car.
            </Confirmation>}
            {(isInsuranceVerificationInProgress || isLoadingOrder) && <CardPreloader />}
          </Card>
        </Col>
      </Row>
    </Container>
  </React.Fragment>
}

export default DesktopContent;
